import { Coordinate, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { companionFloorOffset } from "game-common/room/ember/two_storey_house"
import { ScalingParams } from "../../../client_util"

export class TranslationComputer {
    static readonly PAN_MS = 360
    static readonly FLOOR_PAN_MS = 600
    static readonly JUMP_DISTANCE = TileSize
    static readonly FALL_FOLLOW_MS = 80
    tilemapMetaProvider: TileMapMetaProvider
    modifierX: number
    modifierY: number
    hasRunOnce: boolean = false
    ts: number
    noDelay: boolean = false
    xStatus: "left" | "center" | "right" | undefined
    yStatus: "top" | "center" | "bottom" | undefined
    private target?: Coordinate
    private panOffset?: Coordinate
    private panStartedAt = 0
    private floorPan = false
    private levelChanged = false
    private followingFall = false
    private fallFollowUntil = 0
    private computedAt = 0
    private velocity = { x: 0, y: 0 }

    update = (tilemapMetaProvider: TileMapMetaProvider) => {
        const previous = this.tilemapMetaProvider
        const previousHouse = previous?.twoStoreyHouse
        const nextHouse = tilemapMetaProvider.twoStoreyHouse
        const preserve = previous?.roomId === tilemapMetaProvider.roomId ||
            (previousHouse && nextHouse && previousHouse.groundRoomId === nextHouse.groundRoomId)
        if (preserve && this.hasRunOnce && previousHouse && nextHouse && previousHouse.floor !== nextHouse.floor) {
            // The destination used to be a companion drawn at this vertical offset.
            // Preserve its screen placement before easing toward the new active floor.
            this.modifierY += companionFloorOffset(previousHouse, nextHouse)
            this.levelChanged = true
        }
        this.tilemapMetaProvider = tilemapMetaProvider
        this.ts = Date.now()
        if (!preserve) this.reset()
    }

    reset = () => {
        this.hasRunOnce = false
        this.target = undefined
        this.panOffset = undefined
        this.floorPan = false
        this.levelChanged = false
        this.followingFall = false
        this.fallFollowUntil = 0
        this.computedAt = 0
        this.velocity = { x: 0, y: 0 }
        this.modifierX = undefined
        this.modifierY = undefined
        this.xStatus = undefined
        this.yStatus = undefined
    }

    ready = () => Date.now() - this.ts >= 150

    getCameraCenter = (): Coordinate | undefined => this.hasRunOnce
        ? { x: 50 - this.modifierX, y: 50 - this.modifierY } : undefined

    private bounds = () => {
        const X_FACTOR = ScalingParams.viewportX
        const Y_FACTOR = ScalingParams.viewportY - 1
        return {
            minX: TileSize * X_FACTOR,
            maxX: (this.tilemapMetaProvider.width() - X_FACTOR) * TileSize,
            minY: TileSize * Y_FACTOR,
            maxY: (this.tilemapMetaProvider.height() - Y_FACTOR) * TileSize,
        }
    }

    /** The nearest point the camera can centre on without showing past the map edge. */
    clampFocus = (location: Coordinate): Coordinate => {
        const { minX, maxX, minY, maxY } = this.bounds()
        return {
            x: Math.min(Math.max(location.x, minX), maxX),
            y: Math.min(Math.max(location.y, minY), maxY),
        }
    }

    compute = (location: Coordinate, force?: boolean, fallOffsetY?: number) => {
        if (!this.hasRunOnce && !force && !this.noDelay && !this.ready()) return
        const { minX, maxX, minY, maxY } = this.bounds()
        const focus = this.clampFocus(location)
        const target = { x: -focus.x + 50, y: -focus.y + 50 }
        const now = Date.now()
        const previousX = this.modifierX, previousY = this.modifierY
        const followingFall = fallOffsetY !== undefined
        const fallHandoff = followingFall !== this.followingFall && this.hasRunOnce
        if (fallOffsetY !== undefined) {
            // Follow the same physical height as the body. Independent per-floor pans
            // would decelerate and restart while the fall itself is accelerating.
            target.y -= fallOffsetY
            // On takeover, retain the displayed framing (including walking/aim lead).
            // Decay that framing difference while the target follows the body's height.
            if (fallHandoff) {
                this.panOffset = { x: this.modifierX - target.x, y: this.modifierY - target.y }
                this.panStartedAt = now
                this.floorPan = false
            }
            this.levelChanged = false
        } else if (this.levelChanged || (!this.followingFall && this.target && Math.hypot(target.x - this.target.x, target.y - this.target.y) >= TranslationComputer.JUMP_DISTANCE)) {
            // Retarget from the displayed position, including an unfinished pan.
            this.panOffset = { x: this.modifierX - target.x, y: this.modifierY - target.y }
            this.panStartedAt = now
            this.floorPan = this.levelChanged
            this.levelChanged = false
        }
        this.followingFall = followingFall
        const duration = this.floorPan ? TranslationComputer.FLOOR_PAN_MS : TranslationComputer.PAN_MS
        const progress = Math.min(1, (now - this.panStartedAt) / duration)
        // Floor changes start and finish at rest, so the height adjustment doesn't
        // kick the camera at full speed as soon as the destination arrives.
        const remaining = !this.panOffset ? 0 : this.floorPan
            ? 1 - progress * progress * (3 - 2 * progress)
            : (1 - progress) ** 3
        this.modifierX = target.x + (this.panOffset?.x ?? 0) * remaining
        this.modifierY = target.y + (this.panOffset?.y ?? 0) * remaining
        const elapsed = Math.max(0, now - this.computedAt)
        if (followingFall) this.fallFollowUntil = now + TranslationComputer.PAN_MS
        if (this.hasRunOnce && now < this.fallFollowUntil) {
            // Falling height accelerates and stops abruptly at the ground. Follow it
            // with a short ease, including the landing tail, rather than copying that
            // velocity change straight into the entire world. Use the displayed
            // modifiers, already rebased by update() when a storey changes.
            const settle = followingFall ? 1 : (this.fallFollowUntil - now) / TranslationComputer.PAN_MS
            // Critically damped motion preserves velocity at both handoffs. Reduce
            // the settling time gradually so resumed walking regains normal follow.
            const omega = 2 / (TranslationComputer.FALL_FOLLOW_MS * settle)
            const decay = Math.exp(-omega * elapsed)
            const smooth = (previous: number, target: number, axis: "x" | "y") => {
                const gap = previous - target
                const motion = this.velocity[axis] + omega * gap
                const position = target + (gap + motion * elapsed) * decay
                this.velocity[axis] = (this.velocity[axis] - omega * motion * elapsed) * decay
                return position
            }
            this.modifierX = smooth(previousX, this.modifierX, "x")
            this.modifierY = smooth(previousY, this.modifierY, "y")
        } else if (this.hasRunOnce && elapsed > 0) {
            this.velocity.x = (this.modifierX - previousX) / elapsed
            this.velocity.y = (this.modifierY - previousY) / elapsed
        }
        this.computedAt = now
        if (progress >= 1) this.panOffset = undefined
        this.target = target
        this.hasRunOnce = true
        this.xStatus = location.x <= minX ? "left" : location.x >= maxX ? "right" : "center"
        this.yStatus = location.y <= minY ? "top" : location.y >= maxY ? "bottom" : "center"
    }
}
